import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/errors/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { PlanDataResult } from './plan-data-types';
import { 
  getDefaultPlans, 
  getDefaultPackages
} from './plan-data-utils';
import { Tables } from '@/integrations/supabase/types';

/**
 * Hook for fetching and managing plan data from the database
 */
export const usePlanData = (): PlanDataResult => {
  const [subscriptionPlans, setSubscriptionPlans] = useState(getDefaultPlans());
  const [tokenPackages, setTokenPackages] = useState(getDefaultPackages());
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchPlans = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch live data from ai_credit_plan_allocations table
      const { data, error } = await supabase
        .from('ai_credit_plan_allocations')
        .select('*')
        .order('plan_name');

      if (error) {
        throw error;
      }

      if (data && data.length > 0) {
        // Transform the database data into subscription plans
        const transformedPlans = data.map(plan => ({
          id: plan.plan_name.toLowerCase(),
          name: plan.plan_name,
          description: '', // Remove descriptions
          price: typeof plan.price === 'object' && plan.price && 'usd' in plan.price ? Number(plan.price.usd) || 0 : 0,
          priceId: plan.stripe_price_id || '',
          features: [
            { name: 'AI Features', included: true },
            { name: `${(plan as any).tokens_per_cycle?.toLocaleString() || '0'} Credits per month`, included: true },
            { name: plan.plan_name === 'Free' ? 'Community Support' : 'Priority Support', included: true }
          ],
          aiTokens: (plan as any).tokens_per_cycle || 0,
          billingPeriod: 'month' as const,
          specialDeal: (plan as any).special_deal || null
        }));
        
        // Filter out AI Starter plan
        const filteredPlans = transformedPlans.filter(plan => 
          plan.name.toLowerCase() !== 'ai starter'
        );
        
        // Sort by price (lowest to highest)
        filteredPlans.sort((a, b) => a.price - b.price);
        
        setSubscriptionPlans(filteredPlans);
        setTokenPackages(getDefaultPackages()); // Keep default packages for now
      } else {
        // Fallback to default plans if no data
        setSubscriptionPlans(getDefaultPlans());
        setTokenPackages(getDefaultPackages());
      }
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch plan data';
      setError(errorMessage);
      
      // Fallback to default plans on error
      setSubscriptionPlans(getDefaultPlans());
      setTokenPackages(getDefaultPackages());
      
      toast({
        title: 'Error Loading Plans',
        description: errorMessage,
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  return {
    subscriptionPlans,
    tokenPackages,
    isLoading,
    error,
    refetch: fetchPlans
  };
};

export default usePlanData;
